'use client';

import { useCallback, useState, useEffect, useTransition } from 'react';
import UserService from '@/services/isomorphic/UserService';
import ConfigurationService from '@/services/isomorphic/ConfigurationService';
import { useRouter } from 'next/navigation';
import { useGlobalContext } from './useGlobalContext';

/**
 * Interface for the useLogout hook.
 */
interface IUseLogout {
  /**
   * Async function to log out the user. Clears the cart and redirects to the logout redirect location.
   * @returns A promise that resolves when the user is logged out.
   */
  logOut: () => Promise<void>;
  /**
   * Whether the user is currently logging out.
   */
  loading: boolean;
  /**
   * Whether the user is currently logged in.
   */
  isLoggedIn: boolean;
}

/**
 * Hook housing the logout flow: logging out the user, clearing the cart, and redirecting to the logout redirect location.
 * @returns The logout function, loading state, and logged in state.
 */
export const useLogout = (): IUseLogout => {
  const { user, cart } = useGlobalContext();

  const router = useRouter();

  const [isLoggedIn, setIsLoggedIn] = useState(user?.isLoggedIn ?? false);

  const [isNavigationPending, startRouteTransition] = useTransition();
  const [isLoading, setIsLoading] = useState(!user);

  useEffect(
    function setUser() {
      if (user) {
        setIsLoggedIn(user.isLoggedIn);
        setIsLoading(false);
      }
    },
    [user]
  );

  const logOut = useCallback(async () => {
    setIsLoading(true);

    try {
      await UserService.signOut();
      await cart.empty();
      setIsLoggedIn(false);

      const redirectURL = ConfigurationService.getConfig('user').getSetting(
        'logoutRedirectLocation'
      ).value;

      startRouteTransition(() => {
        router.push(redirectURL);
      });
    } finally {
      setIsLoading(false);
    }
  }, [cart, router]);

  return { logOut, loading: isLoading || isNavigationPending, isLoggedIn };
};
